import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listSymbols, toggleSymbol, setSymbolRange, Symbol as SymbolType, ApiError } from '../api/client';

interface EditingRange {
  ticker: string;
  from: string;
  to: string;
}

export function SymbolsPage() {
  const queryClient = useQueryClient();
  const [editingRange, setEditingRange] = useState<EditingRange | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError, error: queryError } = useQuery({
    queryKey: ['symbols'],
    queryFn: listSymbols,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ ticker, enabled }: { ticker: string; enabled: boolean }) =>
      toggleSymbol(ticker, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['symbols'] });
      setError(null);
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Failed to toggle symbol');
    },
  });

  const rangeMutation = useMutation({
    mutationFn: ({ ticker, from, to }: { ticker: string; from: string; to: string }) =>
      setSymbolRange(ticker, from, to),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['symbols'] });
      setEditingRange(null);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update date range');
    },
  });

  const handleToggle = (symbol: SymbolType) => {
    toggleMutation.mutate({ ticker: symbol.ticker, enabled: !symbol.enabled });
  };

  const handleEditRange = (symbol: SymbolType) => {
    setEditingRange({
      ticker: symbol.ticker,
      from: symbol.availableFrom?.split('T')[0] ?? '',
      to: symbol.availableTo?.split('T')[0] ?? '',
    });
  };

  const handleSaveRange = () => {
    if (!editingRange) return;
    rangeMutation.mutate({
      ticker: editingRange.ticker,
      from: editingRange.from,
      to: editingRange.to,
    });
  };

  const handleCancelRange = () => {
    setEditingRange(null);
  };

  if (isLoading) {
    return (
      <div className="page">
        <h2>Symbols</h2>
        <p>Loading symbols...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="page">
        <h2>Symbols</h2>
        <div className="error-message">
          Failed to load symbols: {queryError instanceof ApiError ? queryError.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  const symbols = data?.data ?? [];

  return (
    <div className="page">
      <h2>Symbols</h2>
      <p style={{ marginBottom: '16px' }}>
        Manage available symbols. Toggle symbols on/off and configure their queryable date ranges.
      </p>

      {error && (
        <div className="error-message" style={{ marginBottom: '12px' }}>
          {error}
        </div>
      )}

      {symbols.length === 0 ? (
        <p>No symbols registered. Import data to add symbols.</p>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Ticker</th>
              <th>Name</th>
              <th>Type</th>
              <th>Enabled</th>
              <th>Available From</th>
              <th>Available To</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {symbols.map((symbol) => (
              <tr key={symbol.id}>
                <td><strong>{symbol.ticker}</strong></td>
                <td>{symbol.name}</td>
                <td>
                  <span className={`badge badge--${symbol.assetType}`}>
                    {symbol.assetType}
                  </span>
                </td>
                <td>
                  <button
                    className={`toggle-btn ${symbol.enabled ? 'toggle-btn--on' : 'toggle-btn--off'}`}
                    onClick={() => handleToggle(symbol)}
                    disabled={toggleMutation.isPending}
                  >
                    {symbol.enabled ? 'ON' : 'OFF'}
                  </button>
                </td>
                <td>
                  {editingRange?.ticker === symbol.ticker ? (
                    <input
                      type="date"
                      value={editingRange.from}
                      onChange={(e) => setEditingRange({ ...editingRange, from: e.target.value })}
                      className="input-field input-field--small"
                    />
                  ) : (
                    symbol.availableFrom ? new Date(symbol.availableFrom).toLocaleDateString() : '-'
                  )}
                </td>
                <td>
                  {editingRange?.ticker === symbol.ticker ? (
                    <input
                      type="date"
                      value={editingRange.to}
                      onChange={(e) => setEditingRange({ ...editingRange, to: e.target.value })}
                      className="input-field input-field--small"
                    />
                  ) : (
                    symbol.availableTo ? new Date(symbol.availableTo).toLocaleDateString() : '-'
                  )}
                </td>
                <td>
                  {editingRange?.ticker === symbol.ticker ? (
                    <div className="action-group">
                      <button
                        className="btn btn--primary btn--small"
                        onClick={handleSaveRange}
                        disabled={rangeMutation.isPending}
                      >
                        {rangeMutation.isPending ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn--secondary btn--small"
                        onClick={handleCancelRange}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn--secondary btn--small"
                      onClick={() => handleEditRange(symbol)}
                    >
                      Edit Range
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
